import { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  ShoppingBag,
  Package,
  Warehouse,
  Gauge,
  Wallet,
  Users,
  UserCog,
  BarChart3,
  Settings,
  Tag,
  Truck,
  FileSpreadsheet,
  RotateCcw,
  PieChart,
  Layers,
  FileText,
  ClipboardList,
  Building,
  FolderKanban,
  ChevronDown,
  ShieldCheck,
  Shield,
  Newspaper,
  Globe,
  Star,
} from 'lucide-react';
import { ROUTES } from '@/lib/config/routes';
import { PERMISSIONS, type Role, type Permission } from '@/lib/config/constants';
import { useAuthStore } from '@/store/authStore';
import { useRolesStore } from '@/store/rolesStore';
import { useSidebarStore } from '@/store/sidebarStore';
import { useSettingsStore } from '@/store/settingsStore';
import { cn } from '@/lib/utils/cn';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: Role[];

  permission?: Permission;
}

interface NavGroup {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
  collapsible?: boolean;
}

const groups: NavGroup[] = [
  {
    label: 'Overview',
    icon: LayoutDashboard,
    collapsible: false,
    items: [
      {
        to: ROUTES.dashboard,
        label: 'Dashboard',
        icon: LayoutDashboard,
        permission: PERMISSIONS.DASHBOARD_VIEW,
      },
    ],
  },
  {
    label: 'Sales',
    icon: ShoppingBag,
    collapsible: true,
    items: [
      { to: ROUTES.pos, label: 'Point of Sale', icon: ShoppingCart, permission: PERMISSIONS.POS },
      { to: ROUTES.sales, label: 'Sales', icon: ShoppingBag, permission: PERMISSIONS.SALES_VIEW },
      {
        to: ROUTES.orders,
        label: 'Orders',
        icon: ClipboardList,
        permission: PERMISSIONS.SALES_VIEW,
      },
      {
        to: ROUTES.receivables,
        label: 'Receivables',
        icon: Wallet,
        permission: PERMISSIONS.SALES_RECEIVABLES,
      },
      {
        to: ROUTES.rentals,
        label: 'Hire Tracking',
        icon: RotateCcw,
        permission: PERMISSIONS.SALES_QUOTES,
      },
      {
        to: ROUTES.refunds,
        label: 'Refunds',
        icon: RotateCcw,
        permission: PERMISSIONS.SALES_REFUND,
      },
      {
        to: ROUTES.quotes,
        label: 'Quotations',
        icon: FileText,
        permission: PERMISSIONS.SALES_QUOTES,
      },
    ],
  },
  {
    label: 'Inventory',
    icon: Warehouse,
    collapsible: true,
    items: [
      {
        to: ROUTES.products,
        label: 'Products',
        icon: Package,
        permission: PERMISSIONS.PRODUCTS_VIEW,
      },
      {
        to: ROUTES.categories,
        label: 'Categories',
        icon: Tag,
        permission: PERMISSIONS.CATEGORIES_VIEW,
      },
      {
        to: ROUTES.purchaseOrders,
        label: 'Purchases',
        icon: FileSpreadsheet,
        permission: PERMISSIONS.PURCHASES_VIEW,
      },
      {
        to: ROUTES.inventory,
        label: 'Stock Levels',
        icon: Gauge,
        permission: PERMISSIONS.STOCK_VIEW,
      },
    ],
  },
  {
    label: 'Suppliers',
    icon: Truck,
    collapsible: true,
    items: [
      {
        to: ROUTES.suppliers,
        label: 'Suppliers',
        icon: Truck,
        permission: PERMISSIONS.SUPPLIERS_VIEW,
      },
    ],
  },
{
    label: 'Website',
    icon: Globe,
    collapsible: true,
    items: [
      {
        to: ROUTES.partners,
        label: 'Clients',
        icon: Building,
      },
      {
        to: ROUTES.reviews,
        label: 'Reviews',
        icon: Star,
      },
      {
        to: ROUTES.discover,
        label: 'Discover',
        icon: Newspaper,
      },
      {
        to: ROUTES.projects,
        label: 'Projects',
        icon: FolderKanban,
      },
    ],
  },
  {
    label: 'Customers',
    icon: Users,
    collapsible: true,
    items: [
      {
        to: ROUTES.customers,
        label: 'Customers',
        icon: Users,
        permission: PERMISSIONS.CUSTOMERS_VIEW,
      },
    ],
  },
  {
    label: 'Expenses',
    icon: Wallet,
    collapsible: true,
    items: [
      {
        to: ROUTES.expenses,
        label: 'Expenses',
        icon: Wallet,
        permission: PERMISSIONS.EXPENSES_VIEW,
      },
      {
        to: ROUTES.expenseCategories,
        label: 'Categories',
        icon: Tag,
        permission: PERMISSIONS.EXPENSES_VIEW,
      },
    ],
  },
  {
    label: 'Employees',
    icon: Users,
    collapsible: true,
    items: [
      {
        to: ROUTES.employees,
        label: 'Employees',
        icon: Users,
        permission: PERMISSIONS.EMPLOYEES_VIEW,
      },
    ],
  },
  {
    label: 'Users',
    icon: UserCog,
    collapsible: true,
    items: [
      { to: ROUTES.users, label: 'Users', icon: UserCog, permission: PERMISSIONS.USERS_VIEW },
      { to: ROUTES.roles, label: 'Roles', icon: ShieldCheck, permission: PERMISSIONS.ROLES_MANAGE },
    ],
  },
  {
    label: 'Reports',
    icon: BarChart3,
    collapsible: true,
    items: [
      {
        to: ROUTES.reportSales,
        label: 'Sales',
        icon: ShoppingBag,
        permission: PERMISSIONS.REPORTS_VIEW,
      },
      {
        to: ROUTES.reportPurchases,
        label: 'Purchases',
        icon: FileSpreadsheet,
        permission: PERMISSIONS.REPORTS_VIEW,
      },
      {
        to: ROUTES.reportExpenses,
        label: 'Expenses',
        icon: Wallet,
        permission: PERMISSIONS.REPORTS_VIEW,
      },
      {
        to: ROUTES.reportStatement,
        label: 'Business Statement',
        icon: PieChart,
        permission: PERMISSIONS.REPORTS_VIEW,
      },
      {
        to: ROUTES.reportInventory,
        label: 'Stock Valuation',
        icon: Layers,
        permission: PERMISSIONS.REPORTS_VIEW,
      },
    ],
  },
  {
    label: 'System',
    icon: Settings,
    collapsible: true,
    items: [
      {
        to: ROUTES.settings,
        label: 'Settings',
        icon: Settings,
        permission: PERMISSIONS.SETTINGS_MANAGE,
      },
      {
        to: ROUTES.auditLogs,
        label: 'Audit Logs',
        icon: Shield,
        permission: PERMISSIONS.AUDIT_VIEW,
      },
    ],
  },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const hasRole = useAuthStore((s) => s.hasRole);

  const rolesCache = useRolesStore((s) => s.roles);
  const hasPermission = useCallback(
    (permission: Permission) => {
      void rolesCache;
      return useAuthStore.getState().hasPermission(permission);
    },
    [rolesCache],
  );
  const collapsed = useSidebarStore((s) => s.collapsed);
  const setCollapsed = useSidebarStore((s) => s.setCollapsed);
  const location = useLocation();

  const creditSalesEnabled = useSettingsStore((s) => s.settings.enableCreditSales ?? false);

  const navGroups = useMemo<NavGroup[]>(() => {
    return groups.map((g) => {
      let items = g.items;
      if (g.label === 'Sales' && !creditSalesEnabled) {
        items = items.filter((i) => i.to !== ROUTES.receivables);
      }
      return items === g.items ? g : { ...g, items };
    });
  }, [creditSalesEnabled]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-30 bg-black/40 backdrop-blur-sm transition lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
      />
      <aside
        data-collapsed={collapsed}
        className={cn(
          'group/sb fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-surface-elevated transition-[transform,width] duration-200 lg:static lg:translate-x-0 lg:data-[collapsed=true]:w-[4.5rem]',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center gap-2.5 border-b border-border px-5 group-data-[collapsed=true]/sb:lg:justify-center group-data-[collapsed=true]/sb:lg:px-0">
          <img
            src="/images/logos/logo.png"
            alt="DB Scaffolding"
            className="h-7 w-auto shrink-0 object-contain group-data-[collapsed=true]/sb:lg:hidden"
          />
          <img
            src="/favicon.png"
            alt="DB Scaffolding"
            className="hidden h-8 w-8 shrink-0 object-contain group-data-[collapsed=true]/sb:lg:block"
          />
        </div>

        <SidebarNav
          groups={navGroups}
          hasRole={hasRole}
          hasPermission={hasPermission}
          currentPath={location.pathname}
          onNavigate={onClose}
          collapsed={collapsed}
          onExpand={() => setCollapsed(false)}
        />
      </aside>
    </>
  );
}

function isUnder(to: string, path: string) {
  return path === to || path.startsWith(`${to}/`);
}

function bestActiveTo(path: string, tos: string[]) {
  let best = '';
  for (const to of tos) {
    if (isUnder(to, path) && to.length > best.length) best = to;
  }
  return best;
}

function SidebarNav({
  groups,
  hasRole,
  hasPermission,
  currentPath,
  onNavigate,
  collapsed,
  onExpand,
}: {
  groups: NavGroup[];
  hasRole: (...roles: Role[]) => boolean;
  hasPermission: (permission: Permission) => boolean;
  currentPath: string;
  onNavigate: () => void;
  collapsed: boolean;
  onExpand: () => void;
}) {
  const activeTo = bestActiveTo(
    currentPath,
    groups.flatMap((g) => g.items.map((i) => i.to)),
  );

  const findActiveGroup = useCallback(
    () =>
      groups.find(
        (g) =>
          g.collapsible &&
          g.items
            .filter(
              (i) =>
                (!i.roles || hasRole(...i.roles)) && (!i.permission || hasPermission(i.permission)),
            )
            .some((i) => i.to === activeTo),
      )?.label ?? null,
    [groups, hasRole, hasPermission, activeTo],
  );

  const [expandedLabel, setExpandedLabel] = useState<string | null>(findActiveGroup);

  useEffect(() => {
    const active = findActiveGroup();
    if (active) setExpandedLabel(active);
  }, [findActiveGroup]);

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
      {groups.map((group) => (
        <SidebarGroup
          key={group.label}
          group={group}
          hasRole={hasRole}
          hasPermission={hasPermission}
          activeTo={activeTo}
          onNavigate={onNavigate}
          expanded={expandedLabel === group.label}
          onToggle={() => {
            if (collapsed) {
              onExpand();
              setExpandedLabel(group.label);
              return;
            }
            setExpandedLabel(expandedLabel === group.label ? null : group.label);
          }}
          onCollapseAll={() => setExpandedLabel(null)}
        />
      ))}
    </nav>
  );
}

const ACTIVE_GRADIENT =
  'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950/30 dark:text-brand-300';
const ACTIVE_SOFT = 'text-brand-700 dark:text-brand-300';
const INACTIVE =
  'text-content-muted hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-950/30 dark:hover:text-brand-300';
const RAIL_HIDE = 'group-data-[collapsed=true]/sb:lg:hidden';
const RAIL_CENTER = 'group-data-[collapsed=true]/sb:lg:justify-center';

function SidebarGroup({
  group,
  hasRole,
  hasPermission,
  activeTo,
  onNavigate,
  expanded,
  onToggle,
  onCollapseAll,
}: {
  group: NavGroup;
  hasRole: (...roles: Role[]) => boolean;
  hasPermission: (permission: Permission) => boolean;
  activeTo: string;
  onNavigate: () => void;
  expanded: boolean;
  onToggle: () => void;
  onCollapseAll: () => void;
}) {
  const visibleItems = group.items.filter(
    (i) => (!i.roles || hasRole(...i.roles)) && (!i.permission || hasPermission(i.permission)),
  );
  const isAnyActive = visibleItems.some((i) => i.to === activeTo);
  const panelId = `nav-group-${group.label.replace(/\s+/g, '-').toLowerCase()}`;

  if (visibleItems.length === 0) return null;

  if (!group.collapsible || visibleItems.length === 1) {
    return (
      <div className="mb-1">
        {visibleItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            title={label}
            onClick={() => {
              onCollapseAll();
              onNavigate();
            }}
            className={cn(
              'flex items-center gap-3 rounded-lg border-l-[3px] border-transparent px-3 py-2.5 text-sm font-medium transition-all duration-150',
              RAIL_CENTER,
              to === activeTo ? ACTIVE_GRADIENT : INACTIVE,
            )}
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className={cn('truncate', RAIL_HIDE)}>{label}</span>
          </NavLink>
        ))}
      </div>
    );
  }

  const GroupIcon = group.icon;

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        title={group.label}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border-l-[3px] border-transparent px-3 py-2.5 text-sm font-medium transition-all duration-150',
          RAIL_CENTER,
          isAnyActive ? ACTIVE_GRADIENT : INACTIVE,
        )}
      >
        <GroupIcon className="h-[18px] w-[18px] shrink-0" />
        <span className={cn('flex-1 truncate text-left', RAIL_HIDE)}>{group.label}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            RAIL_HIDE,
            isAnyActive ? 'text-brand-600 dark:text-brand-300' : 'text-content-subtle',
            expanded && 'rotate-180',
          )}
        />
      </button>

      <div
        id={panelId}
        className={cn(
          'grid transition-all duration-200',
          RAIL_HIDE,
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-3">
            {visibleItems.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150',
                    to === activeTo ? ACTIVE_SOFT : INACTIVE,
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
